import { applyAnytimeDiscount, applyEurolifeDiscount, clearDiscount, DiscountTypes } from '@/api/legacyAPI';
import { CompanyIDs } from '@/core/companyComponents';
import { LocalStorageService } from '@/core/services/localStorageService';

const DC_KEY = 'dc';
const OFFER_CODE_KEY = 'offerCode';
const SELECTED_PRODUCT_KEY = 'selectedProduct';
const APPLIED_DISCOUNT_KEY = 'appliedDiscount';

const startDateConstraintsCache = {};

export const SelectedProductService = () => {

    const getEasyBuyStatus = () => {
        const selectedProduct = LocalStorageService.get(SELECTED_PRODUCT_KEY) ?? {};
        const isEasyBuy = selectedProduct?.payload?.multiproduct?.attributes?.easyBuy ?? false;

        return isEasyBuy
    }

    const setEasyBuyStatus = (status) => {
        const selectedProduct = LocalStorageService.get(SELECTED_PRODUCT_KEY) ?? {};
        if (!selectedProduct?.payload?.multiproduct?.attributes) {
            return;
        }
        selectedProduct.payload.multiproduct.attributes.easyBuy = Boolean(status);
        LocalStorageService.set(SELECTED_PRODUCT_KEY, selectedProduct);
    }

    const getStartDate = () => {
        const { startDate } = LocalStorageService.get(SELECTED_PRODUCT_KEY) ?? {};
        if (!startDate) {
            return new Date();
        }

        const cachedDate = new Date(startDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        return cachedDate < today ? new Date() : cachedDate;
    };

    const getStartDateIsFromProduct = () => {
        const { startDateFromProduct } = LocalStorageService.get(SELECTED_PRODUCT_KEY) ?? {};
        return Boolean(startDateFromProduct);
    };

    const setStartDate = (startDate) => {
        const { startDate: existingStartDate } = LocalStorageService.get(SELECTED_PRODUCT_KEY) ?? {};
        const newStartDate = startDate ? new Date(startDate) : new Date();
        LocalStorageService.set(SELECTED_PRODUCT_KEY, { ...LocalStorageService.get(SELECTED_PRODUCT_KEY), startDate: newStartDate });
    };


    const getPrimaryProduct = (product = null) => {
        const selectedProduct = product ?? LocalStorageService.get(SELECTED_PRODUCT_KEY) ?? {};
        const multiproduct = selectedProduct?.payload?.multiproduct;
        const primaryProductId = multiproduct?.attributes?.primaryProduct;
        const products = Array.isArray(multiproduct?.products) ? multiproduct.products : [];
        const primaryProduct = products.find((p) => p?._id === primaryProductId) ?? null;

        return primaryProduct
    }

    const getSelectedProductVendor = () => {
        const vendor = getPrimaryProduct()?.vendor ?? {};

        return vendor
    }

    const getSelectedProduct = () => {
        let selectedProduct = LocalStorageService.get(SELECTED_PRODUCT_KEY) ?? {}
        return selectedProduct
    }
    
    // only the initial quote load populates this cache.
    const cacheStartDateConstraint = (companyId, packageId, startDate) => {
        if (!companyId || !packageId || !startDate) return;
        const key = `${companyId}_${packageId}`;
        if (!(key in startDateConstraintsCache)) {
            startDateConstraintsCache[key] = startDate;
        }
    };

    const store = (multiproduct, offerCode, dc) => {
        LocalStorageService.set(DC_KEY, { dc });
        LocalStorageService.set(OFFER_CODE_KEY, { offerCode });

        const cacheKey = multiproduct?.company?.id && multiproduct?.packageId
            ? `${multiproduct.company.id}_${multiproduct.packageId}`
            : null;
        const cachedStartDate = cacheKey ? (startDateConstraintsCache[cacheKey] ?? null) : null;
        if (
            cachedStartDate &&
            multiproduct?.startDateFromProduct === false &&
            multiproduct?.startDateWasEmpty === true
        ) {
            multiproduct = {
                ...multiproduct,
                startDate: cachedStartDate,
                startDateFromProduct: true,
            };
        }

        LocalStorageService.set(SELECTED_PRODUCT_KEY, multiproduct);
    }

    const storePayload = (payload) => {
        if (!payload) {
            return;
        }

        const selectedProduct = LocalStorageService.get(SELECTED_PRODUCT_KEY);
        if (!selectedProduct) {
            return;
        }

        LocalStorageService.set(SELECTED_PRODUCT_KEY, {
            ...selectedProduct,
            payload,
        });
    }

    const getOfferCode = () => {
        const stored = LocalStorageService.get(OFFER_CODE_KEY) ?? {};
        const offerCode = stored.offerCode ?? null;

        if (offerCode) {
            LocalStorageService.set(OFFER_CODE_KEY, { offerCode });
        }

        return offerCode;
    }

    const getDc = () => {
        let { dc } = LocalStorageService.get(DC_KEY) ?? {}
        return dc
    }

    const updatePriceValue = (value) => {
        LocalStorageService.update(SELECTED_PRODUCT_KEY, 'price', value);
    }

    const updateMonthlyPriceValue = (value) => {
        LocalStorageService.update(SELECTED_PRODUCT_KEY, 'monthlyPrice', value);
    }

    const updateNumberOfCovers = (value) => {
        LocalStorageService.update(SELECTED_PRODUCT_KEY, 'numberOfCovers', value);
    }

    const updateDc = (dc) => {
        if (!dc) {
            return;
        }
        LocalStorageService.set(DC_KEY, { dc });
    }

    const updateOffercode = (offerCode) => {
        if (!offerCode) {
            return;
        }
        LocalStorageService.set(OFFER_CODE_KEY, { offerCode });
    }

    const updateQuotedProducts = (quotedProducts, updatedPrice) => {
        if (!quotedProducts) {
            return null;
        }
        const selectedProduct = LocalStorageService.get(SELECTED_PRODUCT_KEY);
        
        if (!selectedProduct) {
            return null;
        }
        selectedProduct.payload = selectedProduct.payload ?? {};
        selectedProduct.payload.quotedProducts = quotedProducts;

        if (updatedPrice) {
            updatePriceValue(updatedPrice);
        }
        LocalStorageService.set(SELECTED_PRODUCT_KEY, selectedProduct);
        return selectedProduct;
    }

    const getDiscounts = (product) => {
      const primaryProduct = getPrimaryProduct(product);
      const primaryProductGuid = primaryProduct?.guid;
      const primaryProductName = primaryProduct?.name;

      if (!primaryProductGuid) {
        return false;
      }

      if (!primaryProductName) {
        return false;
      }

      return (
        checkAnytimeDiscounts(primaryProductName) ||
        checkEurolifeDiscounts(primaryProductName)
      );
    }

    const checkAnytimeDiscounts = (primaryProductName) => {

      return primaryProductName.includes('DFY');
    };

    const checkEurolifeDiscounts = (primaryProductName) => {
      return primaryProductName.includes('VODAFONE');

    };

    const applyDiscountFactory = (code, fid, dc) => {
        const vendorId = getSelectedProductVendor()?.guid;
        switch (vendorId) {
            case CompanyIDs.ANYTIME:
                return applyAnytimeDiscount(code, fid, dc);
            case CompanyIDs.EUROLIFE:
                return applyEurolifeDiscount(code, fid, dc);
            default:
                return null;
        }
    }

    const getAppliedDiscount = () => {
        return LocalStorageService.get(APPLIED_DISCOUNT_KEY);
    }

    const storeAppliedDiscount = (offerKey, vendorId, fid, dc) => {
        LocalStorageService.set(APPLIED_DISCOUNT_KEY, { offerKey, vendorId, fid, dc });
    }

    const removeAppliedDiscount = () => {
        LocalStorageService.remove(APPLIED_DISCOUNT_KEY);
    }

    // Removes a coupon stored on the legacy quote
    const clearDiscountFactory = (vendorId, fid, dc) => {
        switch (vendorId) {
            case CompanyIDs.ANYTIME:
                return clearDiscount(fid, dc, DiscountTypes.ANYTIME);
            case CompanyIDs.EUROLIFE:
                return clearDiscount(fid, dc, DiscountTypes.EUROLIFE);
            default:
                return null;
        }
    }

    // Legacy keeps the coupon on the quote (per fid) and copies it into every offer
    // created by initListingData, so it must be cleared before a new packet is selected
    const clearAppliedDiscount = async () => {
        const appliedDiscount = getAppliedDiscount();
        if (!appliedDiscount) {
            return;
        }

        try {
            await clearDiscountFactory(appliedDiscount.vendorId, appliedDiscount.fid, appliedDiscount.dc);
            removeAppliedDiscount();
        } catch {}
    }

    return {
        getEasyBuyStatus,
        getSelectedProductVendor,
        setEasyBuyStatus,
        getOfferCode,
        getPrimaryProduct,
        getDc,
        getSelectedProduct,
        store,
        getStartDate,
        getStartDateIsFromProduct,
        getDiscounts,
        applyDiscountFactory,
        clearDiscountFactory,
        getAppliedDiscount,
        storeAppliedDiscount,
        removeAppliedDiscount,
        clearAppliedDiscount,
        updatePriceValue,
        updateMonthlyPriceValue,
        updateNumberOfCovers,
        updateDc,
        updateQuotedProducts,
        storePayload,
        updateOffercode,
        setStartDate,
        cacheStartDateConstraint
    }
}
